import { generateUuid } from '@federa/utils/crypto/uuid';
import { coalesceStringAllowEmpty } from '@federa/utils/data/coalesce-string-allow-empty';
import { formatDateTimeISO } from '@federa/utils/dates/iso-arithmetic-core';
import { asStringOrUndefined, nonBlankStringOrNull } from '@federa/const/string-guards';
import { scopedLogger } from '@federa/utils/deps/registry';
import { parseAttachmentsFromRaw } from '@federa/utils/conversations/attachment-parsers';
import { PARTICIPANT_DEFAULTS } from '@federa/const/auth-session';
import { recordInstantMs } from '@federa/const/record-instant-order';
import { LOGGER_TAGS } from '@federa/const/logging';
import type { ChatMessageStruct, ChatParticipantStruct } from '@federa/types/conversations-chat-structs';
import type { ConversationId, UserId } from '@federa/types/primitives-identifiers';

const logger = scopedLogger(LOGGER_TAGS.RESOLVE_MESSAGE);

function stringifyObjectMessage(obj: unknown): string {
	try {
		return JSON.stringify(obj);
	} catch (error) {
		logger.debug('Failed to stringify message object, using String conversion', { error });
		return String(obj);
	}
}

export function resolveMessage(raw: Record<string, unknown>): string | null {
	// Try message field first (string or object)
	const messageStr = nonBlankStringOrNull(raw.message);
	if (messageStr) return messageStr;

	if (raw.message && typeof raw.message === 'object') {
		return stringifyObjectMessage(raw.message);
	}

	const content = nonBlankStringOrNull(raw.content);
	if (content) return content;

	const body = nonBlankStringOrNull(raw.body);
	if (body) return body;

	if (raw.data && typeof raw.data === 'object') {
		const nested = nonBlankStringOrNull((raw.data as Record<string, unknown>).message);
		if (nested) return nested;
	}

	return null;
}

export function toChatMessage(raw: Record<string, unknown>, defaultRoomId: string): ChatMessageStruct | null {
	const message = resolveMessage(raw);
	if (!message) return null;

	const timestampRaw = raw.timestamp;
	// A number arrives as epoch ms already; everything else goes through the instant SSOT, which answers null -- a
	// measurement gap -- for a non-string and for an unreadable stamp alike. That is the same ladder the eight lines
	// this replaced walked by hand, including the empty-string case (`Date.parse('')` was NaN, `recordInstantMs('')`
	// is null).
	const timestampMs = typeof timestampRaw === 'number' ? timestampRaw : recordInstantMs(timestampRaw);
	const createdAt = formatDateTimeISO(timestampMs);

	const generatedId = generateUuid();

	const id = coalesceStringAllowEmpty(raw.message_id, raw.messageId) ?? generatedId;
	const senderId = coalesceStringAllowEmpty(raw.userId, raw.user_id) ?? '';
	const senderName = coalesceStringAllowEmpty(raw.name, raw.username) ?? undefined;
	const roomId = coalesceStringAllowEmpty(raw.conversation_id, raw.conversationId, raw.roomId) ?? defaultRoomId;
	const attachments = parseAttachmentsFromRaw(raw);

	return {
		id,
		content: message,
		senderId: senderId as UserId,
		senderName,
		createdAt,
		roomId: roomId as ConversationId,
		attachments: attachments.length > 0 ? attachments : undefined,
	};
}

export function toParticipant(raw: Record<string, unknown>): ChatParticipantStruct {
	let isActive: number | null = null;
	if (typeof raw.is_active === 'number') {
		isActive = raw.is_active;
	} else if (typeof raw.isActive === 'number') {
		isActive = raw.isActive;
	}
	return {
		id: coalesceStringAllowEmpty(raw.id, raw.userId) ?? '',
		name: coalesceStringAllowEmpty(raw.identity, raw.name, raw.email, raw.mfa_phone) ?? PARTICIPANT_DEFAULTS.NAME,
		role: asStringOrUndefined(raw.role),
		email: asStringOrUndefined(raw.email),
		phone: asStringOrUndefined(raw.mfa_phone),
		roomId: (coalesceStringAllowEmpty(raw.conversation_id, raw.conversationId) ?? undefined) as ConversationId | undefined,
		isActive,
	};
}
